/** Shared substrate for per-user UI preferences persisted to
 *  `localStorage`. Concrete preference modules (keyboard overrides,
 *  command-palette recents, future history-cap setting, saved
 *  actions, action templates) call `createPersistedNamespace` instead
 *  of reinventing the safe-storage + defensive-parse pattern.
 *
 *  Two reasons to keep the abstraction this thin:
 *
 *    1. **Defensive parsing lives at the namespace's edge, not in a
 *       shared schema.** Each preference module knows its own shape;
 *       passing the validator inline keeps the parse step under the
 *       module's control (a future schema bump there doesn't ripple
 *       into this file).
 *
 *    2. **Storage fallback is silent.** Safari private mode rejects
 *       `localStorage.setItem` with a quota error; SSR has no
 *       `window`. Both degrade to an in-memory shim — a broken
 *       persistence layer is a degraded experience, never a blocker
 *       for the rest of the editor.
 *
 *  Storage keys SHOULD include a `:v1` (or `:vN`) suffix. When the
 *  persisted shape grows incompatible fields, bump to the next
 *  version and orphan the old key — localStorage quota is in MB; a
 *  stale tens-of-bytes record costs nothing. */

export interface PersistedNamespace<T> {
  /** Read the persisted value, falling back to `defaultValue` for
   *  missing / malformed / quota-rejected reads. Never throws. */
  load(storage?: Storage): T;
  /** Persist a value. Quota / SSR failures are swallowed silently —
   *  see module note. */
  save(value: T, storage?: Storage): void;
  /** Drop the persisted entry entirely. Subsequent `load()` returns
   *  `defaultValue`. */
  clear(storage?: Storage): void;
  /** The localStorage key (exported for tests + the export/import
   *  JSON UI in the keyboard settings dialog). */
  readonly storageKey: string;
}

export interface PersistedNamespaceOptions<T> {
  /** Versioned key, e.g. `image-editor:keyboard-overrides:v1`. */
  storageKey: string;
  /** Returned by `load()` when nothing is persisted or the parse
   *  callback rejects the stored payload. */
  defaultValue: T;
  /** Coerce / validate the JSON-parsed value. Receives the raw
   *  `JSON.parse` result — could be ANY shape because storage is
   *  mutable from devtools and from a future schema-bumping
   *  release. Return a sanitized `T`, or fall back to
   *  `defaultValue` for anything not recoverable. Called on every
   *  load — keep it cheap.
   *
   *  Implementations typically branch on `Array.isArray(raw)` or
   *  `typeof raw === 'object'` first, then walk the structure with
   *  per-field type guards. Never trust the parsed JSON without
   *  validation. */
  parse: (raw: unknown) => T;
}

/** Build a persistence handle for one localStorage namespace.
 *
 *  Each call returns a fresh `{ load, save, clear }` triple wired
 *  to its own key — typical pattern is to call this once at module
 *  scope and re-export the methods. */
export function createPersistedNamespace<T>(
  opts: PersistedNamespaceOptions<T>,
): PersistedNamespace<T> {
  return {
    storageKey: opts.storageKey,
    load(storage: Storage = safeStorage()): T {
      try {
        const raw = storage.getItem(opts.storageKey);
        if (raw === null) return opts.defaultValue;
        const parsed = JSON.parse(raw);
        return opts.parse(parsed);
      } catch {
        return opts.defaultValue;
      }
    },
    save(value: T, storage: Storage = safeStorage()): void {
      try {
        storage.setItem(opts.storageKey, JSON.stringify(value));
      } catch {
        // ignore — see module note
      }
    },
    clear(storage: Storage = safeStorage()): void {
      try {
        storage.removeItem(opts.storageKey);
      } catch {
        // ignore
      }
    },
  };
}

/** Real localStorage in the browser, in-memory shim otherwise.
 *  Exported for callers that want to share one resolved Storage
 *  between multiple namespaces in the same render path (saves a
 *  `typeof window` check per access — rarely worth optimizing for,
 *  but available). */
export function safeStorage(): Storage {
  if (typeof window === 'undefined' || !window.localStorage) {
    return inMemoryStorage();
  }
  return window.localStorage;
}

/** Map-backed Storage implementation. Same semantics as
 *  `window.localStorage` for the methods we use: `getItem` returns
 *  `null` for missing keys, `setItem` overwrites, `removeItem`
 *  silently no-ops on missing keys. Exported for tests that want
 *  to assert against a per-test isolated storage. */
export function inMemoryStorage(): Storage {
  const map = new Map<string, string>();
  return {
    get length() {
      return map.size;
    },
    clear: () => map.clear(),
    getItem: (k: string) => map.get(k) ?? null,
    key: (i: number) => Array.from(map.keys())[i] ?? null,
    removeItem: (k: string) => {
      map.delete(k);
    },
    setItem: (k: string, v: string) => {
      map.set(k, v);
    },
  } as Storage;
}
